import {
  forwardRef, useId, useLayoutEffect, useMemo, useRef, useState,
  type ButtonHTMLAttributes,
} from "react";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";

export type ProjectOption = { id: string; name: string };
type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value" | "onChange"> & {
  options: readonly ProjectOption[];
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  placeholder?: string;
  scopeKey: string;
  allProjects?: { id: string; name: string };
  emptyMessage?: string;
  selectedLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentKey?: string | number;
  onCloseAutoFocus?: (event: Event) => void;
  optionTestIdPrefix?: string;
};

/** Presentation only: consumers supply already-authorized options and own scope safety. */
export const ProjectCombobox = forwardRef<HTMLButtonElement, Props>(function ProjectCombobox({
  options, value, onValueChange, label, placeholder, scopeKey, allProjects,
  emptyMessage, selectedLabel, open: controlledOpen, onOpenChange,
  contentKey, onCloseAutoFocus, optionTestIdPrefix, disabled, className, ...triggerProps
}, forwardedRef) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  type Opening = { content: HTMLElement; trigger: HTMLButtonElement | null; parent: HTMLElement | null };
  const openings = useRef(new WeakMap<HTMLElement, Opening>());
  const latestOpening = useRef<Opening | null>(null);
  const canRestore = useRef(false);
  useLayoutEffect(() => {
    canRestore.current = !open || !!disabled;
    return () => { canRestore.current = false; };
  }, [open, disabled]);
  const eligible = (target: HTMLElement | null): target is HTMLElement => {
    if (!target?.isConnected || target.matches(":disabled, [aria-disabled='true']")
      || target.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    for (let node: HTMLElement | null = target; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden"
        || style.visibility === "collapse" || style.contentVisibility === "hidden") return false;
    }
    return true;
  };
  if (disabled && localOpen) setLocalOpen(false);
  const projects = useMemo(() => Array.from(new Map(
    options.filter((option) => option.id && option.id !== allProjects?.id)
      .map((option) => [option.id, option]),
  ).values()), [options, allProjects?.id]);
  const showSearch = projects.length >= 6;
  const [search, setSearch] = useState({ scopeKey, text: "" });
  if (search.scopeKey !== scopeKey || ((!open || disabled || !showSearch) && search.text)) {
    setSearch({ scopeKey, text: "" });
  }
  const text = showSearch && search.scopeKey === scopeKey ? search.text : "";
  const query = text.trim().toLocaleLowerCase();
  // One filtering mechanism; preserve supplied order and stable IDs.
  const matches = projects.filter((option) =>
    `${option.name} ${option.id}`.toLocaleLowerCase().includes(query),
  );
  const choices = allProjects ? [allProjects, ...matches] : matches;
  const changeOpen = (next: boolean) => {
    if (next && disabled) return;
    if (!next) setSearch({ scopeKey, text: "" });
    setLocalOpen(next);
    onOpenChange?.(next);
  };
  const previousSearch = useRef(showSearch);
  useLayoutEffect(() => {
    const changed = previousSearch.current !== showSearch;
    previousSearch.current = showSearch;
    const content = contentRef.current;
    if (open && !disabled && changed && content?.isConnected && !content.contains(document.activeElement)) {
      (content.querySelector<HTMLElement>(showSearch ? "input" : '[role="option"]') ?? content).focus();
    }
  }, [open, disabled, showSearch]);
  useLayoutEffect(() => {
    // A live update can remove the focused result. Recover only lost focus;
    // never override a deliberate move outside this popup.
    const content = contentRef.current;
    if (open && !disabled && content?.isConnected && lastFocusedRef.current
      && !lastFocusedRef.current.isConnected && document.activeElement === document.body) {
      (content.querySelector<HTMLElement>("input, [role='option']") ?? content).focus();
    }
  });
  const title = selectedLabel ?? (value === allProjects?.id ? allProjects.name
    : projects.find((option) => option.id === value)?.name);

  return (
    <Popover open={open && !disabled} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          {...triggerProps}
          ref={(node) => {
            triggerRef.current = node;
            if (typeof forwardedRef === "function") forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          type="button" value={value} role="combobox" aria-label={label}
          aria-haspopup="listbox" aria-controls={open && !disabled ? listId : undefined}
          data-slot="select-trigger" disabled={disabled}
          onKeyDown={(event) => {
            triggerProps.onKeyDown?.(event);
            if (!event.defaultPrevented && ["ArrowDown", "ArrowUp"].includes(event.key)) {
              event.preventDefault(); changeOpen(true);
            }
          }}
          className={cn("flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-input px-3 text-start text-sm shadow-xs outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50", className)}
        >
          <span className="min-w-0 truncate">{title || placeholder || label}</span>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        key={contentKey} align="start" dir={i18n?.dir?.() ?? "ltr"}
        role="presentation" aria-label={label}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-0"
        onFocusCapture={(event) => {
          contentRef.current = event.currentTarget;
          lastFocusedRef.current = event.target as HTMLElement;
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const content = event.target as HTMLElement;
          contentRef.current = content;
          const trigger = triggerRef.current;
          const opening = { content, trigger, parent: trigger?.closest<HTMLElement>('[role="dialog"]') ?? null };
          openings.current.set(content, opening);
          latestOpening.current = opening;
          (showSearch ? content.querySelector<HTMLElement>("input")
            : content.querySelector<HTMLElement>('[aria-selected="true"]')
              ?? content.querySelector<HTMLElement>('[role="option"]')
              ?? content)?.focus();
        }}
        onCloseAutoFocus={(event) => {
          // Controlled consumers (MCP) retain their own session/fallback policy.
          if (onCloseAutoFocus) { onCloseAutoFocus(event); return; }
          const opening = openings.current.get(event.target as HTMLElement);
          const active = document.activeElement;
          // Radix dispatches this after unmount. A later opening or deliberate
          // focus move owns focus now; an old callback must not take it back.
          if (!canRestore.current || !opening || latestOpening.current !== opening
            || (active && active !== document.body && active !== opening.trigger && !opening.content.contains(active))
            || (opening.parent && (!eligible(opening.parent) || opening.parent.dataset.state !== "open"))) {
            event.preventDefault(); return;
          }
          if (!eligible(opening.trigger)) {
            event.preventDefault();
            // Capture the actual parent at opening, rather than searching the
            // document for a dialog that may belong to a newer workflow.
            if (opening.parent && eligible(opening.parent)) opening.parent.focus();
          }
        }}
        onEscapeKeyDown={(event) => { event.stopPropagation(); }}
        onKeyDown={(event) => {
          // There is no tabbable utility footer. Dismiss instead of cycling the
          // search input inside Radix's looping focus scope.
          if (event.key === "Tab") { event.preventDefault(); changeOpen(false); }
        }}
      >
        {showSearch && (
          <div className="shrink-0 border-b border-border p-2">
            <Input type="search" aria-label={t("projects.searchProjects")}
              aria-controls={listId} placeholder={t("projects.searchProjects")}
              value={text} className="h-9"
              onChange={(event) => setSearch({ scopeKey, text: event.target.value })}
              onKeyDown={(event) => {
                if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                event.preventDefault();
                const items = contentRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
                items?.[event.key === "ArrowDown" ? 0 : items.length - 1]?.focus();
              }} />
          </div>
        )}
        <div id={listId} role="listbox" aria-label={label} data-state="open"
          dir={i18n?.dir?.() ?? "ltr"} data-project-options
          className="min-h-0 max-h-[300px] overflow-y-auto overscroll-contain p-1"
          onKeyDown={(event) => {
            if (!["Home", "End", "ArrowDown", "ArrowUp"].includes(event.key)) return;
            const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]'));
            const index = items.indexOf(document.activeElement as HTMLButtonElement);
            const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
              : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
            event.preventDefault(); items[next]?.focus();
          }}>
          {choices.map((option) => (
            <button key={option.id} type="button" role="option" tabIndex={-1}
              aria-selected={value === option.id} data-project-id={option.id}
              data-testid={optionTestIdPrefix && `${optionTestIdPrefix}-${projects.findIndex((project) => project.id === option.id)}`}
              className="flex min-h-9 w-full items-center gap-2 rounded-sm px-2 py-1.5 text-start text-sm hover:bg-accent focus:bg-accent focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              onClick={() => {
                if (disabled || !choices.some((choice) => choice.id === option.id)) return;
                onValueChange(option.id); changeOpen(false);
              }}>
              <span className="min-w-0 flex-1 whitespace-normal break-words">{option.name || option.id}</span>
              {value === option.id && <Check aria-hidden="true" className="size-4 shrink-0" />}
            </button>
          ))}
          {matches.length === 0 && <div role="status" className="px-3 py-4 text-center text-sm text-muted-foreground">
            {query ? t("projects.noMatchingProjects") : emptyMessage || t("projects.noProjectsAvailable")}
          </div>}
        </div>
      </PopoverContent>
    </Popover>
  );
});
